import { Share as CapacitorShare } from "@capacitor/share";
import { useMutation } from "@tanstack/react-query";

import { Models } from "@/api/types";
import { useTranslation } from "@/core/i18n";
import { postReferrals } from "api/methods";
import { isNativeMobileApp } from "utils/platform.helpers";

type UseLegacyReferAFriendParams = {
  rewardsEnabled: boolean;
  platform: Models<"PlatformModel">;
  title: string;
  formattedPlatformName: string;
  copyToClipboard: (text: string) => void;
  isCopyingLink: boolean;
  referralUrl: string;
  setReferralUrl: (url: string) => void;
};

type UseLegacyReferAFriendResult = {
  shareLegacyReferral: () => void;
  handleCopyLink: () => void;
};

export function useLegacyReferAFriend({
  rewardsEnabled,
  platform,
  title,
  formattedPlatformName,
  copyToClipboard,
  isCopyingLink,
  referralUrl,
  setReferralUrl,
}: UseLegacyReferAFriendParams): UseLegacyReferAFriendResult {
  const { t } = useTranslation();

  let shareText = t("useLegacyReferAFriend.amazingExperienceShareText", {
    formattedPlatformName,
  });

  if (rewardsEnabled && title) {
    shareText = t("useLegacyReferAFriend.rewardsProgramShareText", {
      formattedPlatformName,
      title,
    });
  }

  const mutation = useMutation({
    mutationFn: () => postReferrals({}),
    onSuccess: ({ referralUrl }) => {
      if (referralUrl) {
        setReferralUrl(referralUrl);

        if (isNativeMobileApp()) {
          CapacitorShare.share({
            text: `${shareText} ${referralUrl}`,
          })
            .then(() => console.log(`Successful share. Referral URL: ${referralUrl}`))
            .catch((error) => console.log(`Error sharing. Referral URL: ${referralUrl}`, error));

          return;
        }

        if (navigator.share) {
          navigator
            .share({
              text: `${shareText} ${referralUrl}`,
            })
            .then(() => console.log(`Successful share. Referral URL: ${referralUrl}`))
            .catch((error) => console.log(`Error sharing. Referral URL: ${referralUrl}`, error));

          return;
        }

        globalThis.location.href = `mailto:?body=${encodeURIComponent(shareText)}`;
      }
    },
  });

  const copyLinkMutation = useMutation({
    mutationFn: () => postReferrals({}),
    onSuccess: ({ referralUrl }) => {
      if (referralUrl) {
        setReferralUrl(referralUrl);
        copyToClipboard(referralUrl);
      }
    },
    onError: (error) => {
      console.log("Error generating referral link for copy: ", error);
    },
  });

  const shareLegacyReferral = () => mutation.mutate();

  const handleCopyLink = () => {
    if (!referralUrl) {
      // Generate and copy the link
      copyLinkMutation.mutate();
    } else {
      // Copy existing link immediately
      copyToClipboard(referralUrl);
    }
  };

  return {
    shareLegacyReferral,
    handleCopyLink,
  };
}
